import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

const acceptInviteSchema = z
  .object({
    fullName: z.string().min(1, 'Full name is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type AcceptInviteFormData = z.infer<typeof acceptInviteSchema>

interface InvitationInfo {
  email: string
  role: string
  accountName?: string
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
  })

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid invitation link')
        setIsLoading(false)
        return
      }

      try {
        // Fetch invitation details by token
        const { data: invitation, error: fetchError } = await supabase
          .from('invitations')
          .select(
            `
            *,
            account:accounts(name)
          `
          )
          .eq('token', token)
          .single()

        if (fetchError || !invitation) {
          setError('This invitation link is invalid')
          setIsLoading(false)
          return
        }

        // Check if already accepted
        if (invitation.accepted_at) {
          setError('This invitation has already been used')
          setIsLoading(false)
          return
        }

        // Check if expired
        if (new Date(invitation.expires_at) < new Date()) {
          setError(
            'This invitation has expired. Please contact your administrator for a new invitation.'
          )
          setIsLoading(false)
          return
        }

        setInvitationInfo({
          email: invitation.email,
          role: invitation.role,
          accountName: invitation.account?.name,
        })
        setIsLoading(false)
      } catch {
        setError('Failed to validate invitation')
        setIsLoading(false)
      }
    }

    validateToken()
  }, [token])

  const onSubmit = async (data: AcceptInviteFormData) => {
    if (isSubmitting || !invitationInfo || !token) return
    setIsSubmitting(true)

    try {
      // Complete the invitation server-side. This creates the user with the
      // email already confirmed (no confirmation email is sent) and provisions
      // the profile deterministically, then we sign the user straight in.
      const { data: result, error: acceptError } =
        await supabase.functions.invoke('accept-invitation', {
          body: {
            token,
            password: data.password,
            full_name: data.fullName,
          },
        })

      if (acceptError || result?.error) {
        const message =
          result?.error || acceptError?.message || 'Failed to create account'
        toast({ title: 'Error', description: message, variant: 'destructive' })
        return
      }

      // Email is pre-confirmed, so sign in immediately for a seamless handoff.
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitationInfo.email,
        password: data.password,
      })

      toast({ title: 'Account created successfully!' })
      navigate(signInError ? '/login' : '/', { replace: true })
    } catch {
      toast({ title: 'Error', description: 'An unexpected error occurred', variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center">
        <h2 className="mb-4 text-2xl font-semibold">Invalid Invitation</h2>
        <p className="mb-6 text-muted-foreground">{error}</p>
        <Link
          to="/login"
          className="text-sm font-medium text-primary hover:underline"
        >
          Go to Sign In
        </Link>
      </div>
    )
  }

  return (
    <div>
      <h2 className="mb-2 text-center text-2xl font-semibold">
        Accept Invitation
      </h2>
      <p className="mb-6 text-center text-sm text-muted-foreground">
        You've been invited to join
        {invitationInfo?.accountName
          ? ` ${invitationInfo.accountName}`
          : ' the platform'}
        {invitationInfo?.role === 'admin' ? ' as an administrator' : ''}.
      </p>

      <div className="mb-4 rounded-md bg-muted p-3 text-center text-sm">
        <span className="text-muted-foreground">Email: </span>
        <span className="font-medium">{invitationInfo?.email}</span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="fullName"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Full Name
          </label>
          <input
            id="fullName"
            type="text"
            autoComplete="name"
            {...register('fullName')}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="John Doe"
            disabled={isSubmitting}
          />
          {errors.fullName && (
            <p className="mt-1 text-sm text-destructive">
              {errors.fullName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="password"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="••••••••"
            disabled={isSubmitting}
          />
          {errors.password && (
            <p className="mt-1 text-sm text-destructive">
              {errors.password.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            {...register('confirmPassword')}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="••••••••"
            disabled={isSubmitting}
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-destructive">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>
    </div>
  )
}
